import { z } from "zod";

/**
 * The validated contract every {@link DocExtractor} implementation must return.
 * Skills here are still raw strings — normalization to the canonical taxonomy
 * happens downstream (never store free-text skills; see ARCHITECTURE §7.6).
 */

export const ContactSchema = z.object({
  name: z.string().nullable().default(null),
  // Degrade a malformed email to null rather than failing the whole parse (S7).
  email: z.string().email().nullable().default(null).catch(null),
  phone: z.string().nullable().default(null),
  location: z.string().nullable().default(null),
});

export const WorkExperienceSchema = z.object({
  company: z.string(),
  title: z.string(),
  startDate: z.string().nullable().default(null),
  endDate: z.string().nullable().default(null),
  current: z.boolean().default(false),
  description: z.string().nullable().default(null),
  highlights: z.array(z.string()).default([]),
});

export const EducationSchema = z.object({
  institution: z.string(),
  degree: z.string().nullable().default(null),
  field: z.string().nullable().default(null),
  startDate: z.string().nullable().default(null),
  endDate: z.string().nullable().default(null),
});

export const CertificationSchema = z.object({
  name: z.string(),
  issuer: z.string().nullable().default(null),
  issueDate: z.string().nullable().default(null),
  credentialId: z.string().nullable().default(null),
  // Degrade a malformed URL to null rather than failing the whole parse (S7).
  url: z.string().url().nullable().default(null).catch(null),
});

export const ParsedResumeSchema = z.object({
  contact: ContactSchema.default({}),
  summary: z.string().nullable().default(null),
  work: z.array(WorkExperienceSchema).default([]),
  education: z.array(EducationSchema).default([]),
  /** Raw skill strings as extracted — normalized later. */
  skills: z.array(z.string()).default([]),
  certifications: z.array(CertificationSchema).default([]),
});

export type Contact = z.infer<typeof ContactSchema>;
export type WorkExperience = z.infer<typeof WorkExperienceSchema>;
export type Education = z.infer<typeof EducationSchema>;
export type Certification = z.infer<typeof CertificationSchema>;
export type ParsedResume = z.infer<typeof ParsedResumeSchema>;

/** Resume ingestion lifecycle (mirrors `resumes.status`, ARCHITECTURE §5.3). */
export const ResumeStatus = z.enum(["uploaded", "parsing", "parsed", "edited", "failed"]);
export type ResumeStatus = z.infer<typeof ResumeStatus>;

/** Upload MIME types we accept for resume ingestion (REQ-2.1). */
export const RESUME_MIME_TYPES = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
} as const;
export type ResumeMime = (typeof RESUME_MIME_TYPES)[keyof typeof RESUME_MIME_TYPES];

/** One queue message = one uploaded resume to fetch, extract, and parse (M4). */
export interface ResumeParseMessage {
  resumeId: string;
  userId: string;
  r2Key: string;
  mime: string;
}
